package diana.soleil.design.iraniancanadienbazzar.feature.business

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import diana.soleil.design.iraniancanadienbazzar.core.common.AnalyticsEvents
import diana.soleil.design.iraniancanadienbazzar.core.common.AnalyticsParams
import diana.soleil.design.iraniancanadienbazzar.core.common.AnalyticsTracker
import diana.soleil.design.iraniancanadienbazzar.core.common.AppPreferences
import diana.soleil.design.iraniancanadienbazzar.core.common.AppError
import diana.soleil.design.iraniancanadienbazzar.core.common.DataResult
import diana.soleil.design.iraniancanadienbazzar.core.data.BusinessRepository
import diana.soleil.design.iraniancanadienbazzar.core.data.CategoryRepository
import diana.soleil.design.iraniancanadienbazzar.core.data.ListingMetricsRepository
import diana.soleil.design.iraniancanadienbazzar.core.data.ListingRequestRepository
import diana.soleil.design.iraniancanadienbazzar.core.data.ReportRepository
import diana.soleil.design.iraniancanadienbazzar.core.data.ReviewRepository
import diana.soleil.design.iraniancanadienbazzar.core.data.CityController
import diana.soleil.design.iraniancanadienbazzar.core.data.FavoritesController
import diana.soleil.design.iraniancanadienbazzar.core.data.FollowRepository
import diana.soleil.design.iraniancanadienbazzar.core.data.LocationController
import diana.soleil.design.iraniancanadienbazzar.core.data.SponsorshipConfig
import diana.soleil.design.iraniancanadienbazzar.core.model.Business
import diana.soleil.design.iraniancanadienbazzar.core.model.Category
import diana.soleil.design.iraniancanadienbazzar.core.model.DiscoveryRanking
import diana.soleil.design.iraniancanadienbazzar.core.model.GeoPoint
import diana.soleil.design.iraniancanadienbazzar.core.model.ListingMetric
import diana.soleil.design.iraniancanadienbazzar.core.model.ListingRequestType
import diana.soleil.design.iraniancanadienbazzar.core.model.Review
import kotlinx.coroutines.ExperimentalCoroutinesApi
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.SharingStarted
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.flatMapLatest
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.flow.stateIn
import kotlinx.coroutines.launch

/** UI state for the business listing (sponsored shown first, then the rest). */
data class BusinessListUiState(
    val loading: Boolean = true,
    val sponsored: List<Business> = emptyList(),
    val nearby: List<Business> = emptyList(),
    /** Where "nearby" is measured from (GPS fix or metro centre); null = distance is unknown. */
    val origin: GeoPoint? = null,
    val error: Boolean = false,
)

/**
 * Lists businesses for [categoryId] from [BusinessRepository], split into sponsored vs the rest and
 * scoped to the active metro from [CityController] (re-queries live when the metro changes). Folds in
 * the saved ids from [FavoritesController] so the card hearts render and toggle live.
 */
@OptIn(ExperimentalCoroutinesApi::class)
class BusinessListViewModel(
    private val businessRepository: BusinessRepository,
    private val cityController: CityController,
    private val sponsorshipConfig: SponsorshipConfig,
    categoryRepository: CategoryRepository,
    private val favoritesController: FavoritesController,
    private val locationController: LocationController,
    private val categoryId: String,
) : ViewModel() {

    private val retryTrigger = MutableStateFlow(0)

    /** Saved (favorited) business ids; cards render filled hearts for these and toggle live. */
    val favorites: StateFlow<Set<String>> = favoritesController.favorites

    /** Save/unsave a business (persisted; guests stay device-local). Reflected via [favorites]. */
    fun toggleFavorite(businessId: String) = favoritesController.toggle(businessId)

    /** The category being listed (for its localized display name); null until loaded. */
    val category: StateFlow<Category?> =
        categoryRepository.observeCategories()
            .map { result ->
                (result as? DataResult.Success)?.data?.firstOrNull { it.id == categoryId }
            }
            .stateIn(viewModelScope, SharingStarted.WhileSubscribed(5_000), null)

    val state: StateFlow<BusinessListUiState> =
        combine(
            combine(
                cityController.activeMetroId,
                cityController.activeCountry,
                retryTrigger,
            ) { metroId, country, _ -> metroId to country }
                .flatMapLatest { (metroId, country) -> listFlow(metroId, country.code) },
            sponsorshipConfig.slotsMax,
        ) { ui, slotsMax -> ui.capSponsored(slotsMax) }
            .stateIn(viewModelScope, SharingStarted.WhileSubscribed(5_000), BusinessListUiState())

    fun retry() {
        retryTrigger.value += 1
    }

    private fun listFlow(metroId: String?, countryCode: String): Flow<BusinessListUiState> =
        combine(
            businessRepository.observeBusinesses(metroId = metroId, categoryId = categoryId, country = countryCode),
            // Re-ranks in place once the GPS fix lands (metro centre until then / when denied).
            locationController.location,
        ) { result, userLocation ->
            when (result) {
                is DataResult.Success -> {
                    // Appeal-first ranking (photo → nearby → rating) for both sections; the sponsored
                    // slot cap is applied downstream (reactive to RC).
                    val origin = DiscoveryRanking.origin(userLocation, metroId)
                    val order = DiscoveryRanking.businessComparator(origin)
                    // Mahaleh Pro owners get a boost within the non-sponsored list (above free listings,
                    // but still below paid sponsored slots), then the usual appeal ranking.
                    val restOrder = compareByDescending<Business> { it.ownerPro }.then(order)
                    val (sponsored, rest) = result.data.partition { it.sponsorship.active }
                    BusinessListUiState(
                        loading = false,
                        sponsored = sponsored.sortedWith(order),
                        nearby = rest.sortedWith(restOrder),
                        origin = origin,
                    )
                }
                is DataResult.Failure -> BusinessListUiState(loading = false, error = true)
            }
        }

    /** Keep at most [slotsMax] sponsored placements; the overflow falls back into the normal list. */
    private fun BusinessListUiState.capSponsored(slotsMax: Int): BusinessListUiState {
        if (loading || error || sponsored.size <= slotsMax) return this
        val kept = sponsored.take(slotsMax.coerceAtLeast(0))
        val overflow = sponsored.drop(slotsMax.coerceAtLeast(0))
        return copy(sponsored = kept, nearby = overflow + nearby)
    }
}

/** UI state for the business detail screen. */
data class BusinessDetailUiState(
    val loading: Boolean = true,
    val business: Business? = null,
    val error: Boolean = false,
    val isSaved: Boolean = false,
    val isFollowing: Boolean = false,
    /** True when the signed-in user owns this listing — hides self-inapplicable actions (write review). */
    val isOwnBusiness: Boolean = false,
    /** Set when a follow attempt needs a real account; the route routes to sign-in then consumes it. */
    val followSignInRequired: Boolean = false,
    /** Publicly visible reviews, newest first (live from `reviews`; empty until they stream in). */
    val reviews: List<Review> = emptyList(),
    val claimStatus: ClaimUiStatus = ClaimUiStatus.Idle,
    val reportStatus: ReportUiStatus = ReportUiStatus.Idle,
    val listingRequestStatus: ListingRequestUiStatus = ListingRequestUiStatus.Idle,
)

enum class ReportUiStatus { Idle, Sending, Sent, SignInRequired, Failed }

/** No-login "remove/correct my listing" request lifecycle (no sign-in gate — works for anyone). */
enum class ListingRequestUiStatus { Idle, Sending, Sent, Failed }

enum class ClaimUiStatus { Idle, Requesting, Requested, SignInRequired, Failed }

/**
 * Loads a single [Business] by id for the detail screen and folds in the saved/favorite state from
 * [FavoritesController] (persisted locally today; `users/{uid}` later) so the bookmark toggles live.
 */
class BusinessDetailViewModel(
    private val businessRepository: BusinessRepository,
    private val favoritesController: FavoritesController,
    private val followRepository: FollowRepository,
    reviewRepository: ReviewRepository,
    private val reportRepository: ReportRepository,
    private val listingRequestRepository: ListingRequestRepository,
    private val listingMetrics: ListingMetricsRepository,
    private val analytics: AnalyticsTracker,
    private val preferences: AppPreferences,
    private val businessId: String,
) : ViewModel() {

    /**
     * Review-nudge flywheel: after the user shows real intent (call/directions/chat/website) we ask
     * once — and only once per business, per device — for a review on their way back. Contact
     * actions are the strongest "actually visited" signal we have without purchase data.
     */
    private val _reviewNudge = MutableStateFlow(false)
    val reviewNudge: StateFlow<Boolean> = _reviewNudge.asStateFlow()

    fun noteContactAction() {
        if (businessId !in preferences.reviewNudgeShownIds) _reviewNudge.value = true
    }

    /** Hides the nudge and never re-asks for this business on this device. */
    fun dismissReviewNudge() {
        preferences.reviewNudgeShownIds = preferences.reviewNudgeShownIds + businessId
        _reviewNudge.value = false
    }

    private val loadState = MutableStateFlow(BusinessDetailUiState())

    val state: StateFlow<BusinessDetailUiState> =
        combine(
            loadState,
            favoritesController.favorites,
            reviewRepository.observeVisibleReviews(businessId),
            followRepository.observeFollowing(),
        ) { s, favorites, reviews, following ->
            s.copy(
                isSaved = businessId in favorites,
                isFollowing = businessId in (following as? DataResult.Success)?.data.orEmpty(),
                isOwnBusiness = s.business?.ownerUid != null
                    && s.business.ownerUid == businessRepository.currentUid,
                reviews = (reviews as? DataResult.Success)?.data.orEmpty(),
            )
        }.stateIn(viewModelScope, SharingStarted.WhileSubscribed(5_000), BusinessDetailUiState())

    init {
        load()
        recordMetric(ListingMetric.View)
    }

    /** Best-effort owner-facing engagement counter (server-incremented; never blocks or errors UI). */
    fun recordMetric(metric: ListingMetric) {
        viewModelScope.launch { listingMetrics.record(businessId, metric) }
    }

    fun retry() {
        loadState.value = BusinessDetailUiState(loading = true)
        load()
    }

    /** Save/unsave this business (persisted). Reflected back through [state] via the favorites flow. */
    fun toggleSave() {
        favoritesController.toggle(businessId)
        analytics.track(
            AnalyticsEvents.BUSINESS_SAVE,
            mapOf(
                AnalyticsParams.BUSINESS_ID to businessId,
                AnalyticsParams.SAVED to favoritesController.isFavorite(businessId),
            ),
        )
    }

    /** Follow/unfollow this business (requires a real account). Reflected via the following flow;
     *  guests get [followSignInRequired] so the route can send them to sign-in. */
    fun toggleFollow() {
        val follow = !state.value.isFollowing
        viewModelScope.launch {
            val result = followRepository.setFollowing(businessId, follow)
            if (result is DataResult.Failure && result.error is AppError.Unauthorized) {
                loadState.value = loadState.value.copy(followSignInRequired = true)
            } else if (result is DataResult.Success) {
                analytics.track(
                    AnalyticsEvents.BUSINESS_FOLLOW,
                    mapOf(AnalyticsParams.BUSINESS_ID to businessId, AnalyticsParams.FOLLOWING to follow),
                )
            }
        }
    }

    fun consumeFollowGate() {
        if (loadState.value.followSignInRequired) {
            loadState.value = loadState.value.copy(followSignInRequired = false)
        }
    }

    /** KPI: contact-action taps (call/directions/website) — invoked by the route's handlers. */
    fun trackContactAction(event: String) {
        noteContactAction()
        analytics.track(event, mapOf(AnalyticsParams.BUSINESS_ID to businessId))
    }

    /** Asks to claim this (unclaimed) listing; an admin grants it from the admin queue. */
    fun requestClaim() {
        if (loadState.value.claimStatus == ClaimUiStatus.Requesting) return
        viewModelScope.launch {
            loadState.value = loadState.value.copy(claimStatus = ClaimUiStatus.Requesting)
            loadState.value = loadState.value.copy(
                claimStatus = when (val result = businessRepository.requestClaim(businessId)) {
                    is DataResult.Success -> ClaimUiStatus.Requested
                    is DataResult.Failure ->
                        if (result.error is AppError.Unauthorized) ClaimUiStatus.SignInRequired
                        else ClaimUiStatus.Failed
                },
            )
        }
    }

    fun consumeClaimGate() {
        if (loadState.value.claimStatus == ClaimUiStatus.SignInRequired) {
            loadState.value = loadState.value.copy(claimStatus = ClaimUiStatus.Idle)
        }
    }

    /** Files a moderation report on this business (admin queue picks it up). */
    fun report(reason: String) {
        if (loadState.value.reportStatus == ReportUiStatus.Sending) return
        viewModelScope.launch {
            loadState.value = loadState.value.copy(reportStatus = ReportUiStatus.Sending)
            loadState.value = loadState.value.copy(
                reportStatus = when (val result = reportRepository.fileReport("business", businessId, reason)) {
                    is DataResult.Success -> ReportUiStatus.Sent
                    is DataResult.Failure ->
                        if (result.error is AppError.Unauthorized) ReportUiStatus.SignInRequired
                        else ReportUiStatus.Failed
                },
            )
        }
    }

    fun resetReport() {
        loadState.value = loadState.value.copy(reportStatus = ReportUiStatus.Idle)
    }

    /** No-login privacy request: ask us to remove/correct this listing. Works without an account. */
    fun submitListingRequest(type: ListingRequestType, message: String, email: String) {
        if (loadState.value.listingRequestStatus == ListingRequestUiStatus.Sending) return
        val business = loadState.value.business ?: return
        viewModelScope.launch {
            loadState.value = loadState.value.copy(listingRequestStatus = ListingRequestUiStatus.Sending)
            val result = listingRequestRepository.submit(
                businessId = businessId,
                businessName = business.name,
                type = type,
                message = message,
                contactEmail = email.ifBlank { null },
            )
            loadState.value = loadState.value.copy(
                listingRequestStatus = if (result is DataResult.Success) {
                    ListingRequestUiStatus.Sent
                } else {
                    ListingRequestUiStatus.Failed
                },
            )
        }
    }

    fun resetListingRequest() {
        loadState.value = loadState.value.copy(listingRequestStatus = ListingRequestUiStatus.Idle)
    }

    private fun load() {
        viewModelScope.launch {
            loadState.value = when (val result = businessRepository.getBusiness(businessId)) {
                is DataResult.Success -> {
                    analytics.track(
                        AnalyticsEvents.BUSINESS_VIEW,
                        buildMap {
                            put(AnalyticsParams.BUSINESS_ID, businessId)
                            put(AnalyticsParams.CATEGORY_ID, result.data.categoryId)
                            result.data.metroId?.let { put(AnalyticsParams.METRO_ID, it) }
                        },
                    )
                    BusinessDetailUiState(loading = false, business = result.data)
                }
                is DataResult.Failure -> BusinessDetailUiState(loading = false, error = true)
            }
        }
    }
}
